var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BufferAttribute, BufferGeometry, Color, DoubleSide, Matrix4, Mesh, Uint32BufferAttribute, } from "three";
// @ts-ignore
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils";
import { IfcAPI } from "web-ifc";
import { PickingMeshStandardMaterial } from "../../materials";
import { FileLoader } from "./FileLoader";
/**
 * IFC model file loader implementation
 */
export class IfcLoader {
    static load(url, options) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const objectId = (_a = options === null || options === void 0 ? void 0 : options.objectId) !== null && _a !== void 0 ? _a : 0;
            const materialGroups = new Map();
            // Load the model and recenter
            const ifcApi = new IfcAPI();
            ifcApi.SetWasmPath((_b = IfcLoader.IFC_WASM_PATH) !== null && _b !== void 0 ? _b : "./", true);
            yield ifcApi.Init();
            const buffer = yield FileLoader.loadArrayBuffer(url, { cacheKey: options === null || options === void 0 ? void 0 : options.cacheKey, cache: true });
            const modelId = yield ifcApi.OpenModel(new Uint8Array(buffer), {
                COORDINATE_TO_ORIGIN: true,
                USE_FAST_BOOLS: false,
                CIRCLE_SEGMENTS: 60,
            });
            // Load the geometry and add each to the group
            const ifcMeshes = ifcApi.LoadAllGeometry(modelId);
            const ifcMeshesSize = ifcMeshes.size();
            for (let i = 0; i < ifcMeshesSize; i++) {
                const ifcMesh = ifcMeshes.get(i);
                const geometriesSize = ifcMesh.geometries.size();
                // Read the element data
                const { expressID } = ifcApi.GetLine(modelId, ifcMesh.expressID);
                // Read the geometry data
                for (let j = 0; j < geometriesSize; j++) {
                    const ifcGeometry = ifcMesh.geometries.get(j);
                    IfcLoader.addToMaterialGroup(ifcApi, objectId, modelId, expressID, ifcGeometry, materialGroups);
                }
            }
            // Generate the final mesh
            const mesh = IfcLoader.createMesh(materialGroups);
            const rangeGroups = IfcLoader.createRangeGroups(mesh);
            // Compute the inverse coordination matrix and apply to the mesh
            const matrixArray = ifcApi.GetCoordinationMatrix(modelId);
            const transform = new Matrix4().fromArray(matrixArray).invert();
            transform.decompose(mesh.position, mesh.quaternion, mesh.scale);
            mesh.updateMatrix();
            return { ifcApi, modelId, mesh, rangeGroups };
        });
    }
    /**
     * Set the path to the web-ifc WASM file
     *
     * @param wasmRootPath
     */
    static init(wasmRootPath) {
        return __awaiter(this, void 0, void 0, function* () {
            if (wasmRootPath) {
                IfcLoader.IFC_WASM_PATH = wasmRootPath;
            }
        });
    }
    static createRangeGroups(mesh) {
        var _a, _b;
        const rangeGroups = new Map();
        const elementIds = mesh.geometry.getAttribute("pickingUserId");
        const indices = mesh.geometry.index.array;
        let start = 0;
        let count = 0;
        let currentElementId = elementIds.array[indices[0]];
        for (let i = 0; i < indices.length; i++) {
            const index = indices[i];
            const elementId = elementIds.array[index];
            if (elementId === currentElementId) {
                count++;
            }
            else {
                // Update range list for the elementId
                const ranges = (_a = rangeGroups.get(currentElementId)) !== null && _a !== void 0 ? _a : [];
                ranges.push({ start, count });
                rangeGroups.set(currentElementId, ranges);
                // Start a new range
                start = i;
                count = 1;
                currentElementId = elementId;
            }
        }
        // Handle last range
        const ranges = (_b = rangeGroups.get(currentElementId)) !== null && _b !== void 0 ? _b : [];
        ranges.push({ start, count });
        rangeGroups.set(currentElementId, ranges);
        return rangeGroups;
    }
    static createMesh(materialGroups) {
        const geometries = [];
        const materials = [];
        materialGroups.forEach((group) => {
            geometries.push(mergeGeometries(group.geometries));
            group.geometries.forEach((geometry) => geometry.dispose());
            materials.push(group.material);
        });
        const combinedGeometry = mergeGeometries(geometries, true);
        combinedGeometry.computeBoundsTree();
        combinedGeometry.computeBoundingSphere();
        geometries.forEach((geometry) => geometry.dispose());
        return new Mesh(combinedGeometry, materials);
    }
    static addToMaterialGroup(ifcApi, objectId, modelId, expressId, placedGeometry, materialGroups) {
        var _a;
        const transform = new Matrix4().fromArray(placedGeometry.flatTransformation);
        const geometry = IfcLoader.getBufferGeometry(ifcApi, modelId, expressId, placedGeometry);
        geometry.applyMatrix4(transform);
        const { x: r, y: g, z: b, w: a } = placedGeometry.color;
        const material = new PickingMeshStandardMaterial({
            objectId,
            color: new Color().setRGB(r, g, b, "srgb"),
            opacity: a,
            transparent: a < 1,
            depthWrite: a === 1,
            userIdChannel: true,
            side: DoubleSide,
            forceSinglePass: true,
        });
        const key = `${r},${g},${b},${a}`;
        const group = (_a = materialGroups.get(key)) !== null && _a !== void 0 ? _a : {
            material,
            geometries: [],
        };
        group.geometries.push(geometry);
        materialGroups.set(key, group);
    }
    static getBufferGeometry(ifcApi, modelId, expressId, placedGeometry) {
        const geometry = ifcApi.GetGeometry(modelId, placedGeometry.geometryExpressID);
        const verts = ifcApi.GetVertexArray(geometry.GetVertexData(), geometry.GetVertexDataSize());
        const indices = ifcApi.GetIndexArray(geometry.GetIndexData(), geometry.GetIndexDataSize());
        const buffer = IfcLoader.geometryToBuffer(expressId, verts, indices);
        //@ts-ignore
        geometry.delete();
        return buffer;
    }
    static geometryToBuffer(expressId, vertices, indices) {
        const geometry = new BufferGeometry();
        const positions = new Float32Array(vertices.length / 2);
        const normals = new Float32Array(vertices.length / 2);
        const userIds = new Uint32Array(vertices.length / 6);
        for (let i = 0; i < vertices.length; i += 6) {
            positions[i / 2] = vertices[i];
            positions[i / 2 + 1] = vertices[i + 1];
            positions[i / 2 + 2] = vertices[i + 2];
            normals[i / 2] = vertices[i + 3];
            normals[i / 2 + 1] = vertices[i + 4];
            normals[i / 2 + 2] = vertices[i + 5];
            userIds[i / 6] = expressId;
        }
        // Set the vertex attributes
        geometry.setAttribute("position", new BufferAttribute(positions, 3));
        geometry.setAttribute("normal", new BufferAttribute(normals, 3));
        geometry.setAttribute("pickingUserId", new Uint32BufferAttribute(userIds, 1, false));
        geometry.setIndex(new Uint32BufferAttribute(indices, 1));
        return geometry;
    }
}
IfcLoader.IFC_WASM_PATH = "./";
